'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const categories = [
{
  id: 'body-butters',
  label: 'Natural Body Butters',
  sub: 'Handcrafted in small batches · 8 signature scents',
  href: '/products?category=body-butters',
  image: "/assets/images/template/product-01.webp",
  alt: 'SweetSkyn natural body butter jar — signature line',
  span: 'row-span-2'
},
{
  id: 'scrubs',
  label: 'Exfoliating Scrubs',
  sub: 'Coming soon',
  href: '/products?category=scrubs',
  image: "/assets/images/template/product-06.webp",
  alt: 'SweetSkyn exfoliating scrub line — coming soon',
  span: ''
},
{
  id: 'serums',
  label: 'Facial Serums',
  sub: 'Coming soon',
  href: '/products?category=serums',
  image: "/assets/images/template/product-10.webp",
  alt: 'SweetSkyn facial serum line — coming soon',
  span: ''
},
{
  id: 'full-line',
  label: 'Shop the Butter Collection',
  sub: 'All 8 scents · 4oz and 8oz jars · J$2,000 – J$3,800',
  href: '/products',
  image: "/assets/images/template/product-09.webp",
  alt: 'SweetSkyn full body butter collection',
  span: 'col-span-2'
}];


export default function CategoriesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el, i) => {
              setTimeout(() => {
                el.classList.remove('reveal-hidden');
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 px-6 md:px-10 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
        <div className="reveal reveal-hidden">
          <span className="section-label block mb-3">Curated Collections</span>
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-foreground">
            Shop by category
          </h2>
        </div>
        <Link
          href="/products"
          className="reveal reveal-hidden text-[11px] font-semibold uppercase tracking-widest text-foreground border-b border-foreground pb-0.5 hover:text-accent hover:border-accent transition-colors self-start md:self-auto">
          
          View All Products →
        </Link>
      </div>
      {/* BENTO GRID AUDIT:
           Array: [Skincare(row-span-2), Makeup, Fragrance, Haircare(col-span-2)]
           Desktop (grid-cols-3):
           Row 1: [col-1: Skincare rs-2] [col-2: Makeup cs-1] [col-3: Fragrance cs-1]
           Row 2: [col-1: (Skincare continues)] [col-2: Haircare cs-2]
           Placed 4/4 ✓
        */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {/* Skincare — row-span-2 */}
        <Link
          href={categories?.[0]?.href}
          className="reveal reveal-hidden group relative overflow-hidden md:row-span-2 aspect-[3/4] md:aspect-auto min-h-[300px] md:min-h-[560px] card-rounded">
          
          <AppImage
            src={categories?.[0]?.image}
            alt={categories?.[0]?.alt}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            className="object-cover product-card-img" />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 md:p-8">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">{categories?.[0]?.sub}</span>
            <h3 className="font-display text-3xl md:text-4xl font-light text-white">{categories?.[0]?.label}</h3>
            <span className="inline-flex items-center gap-2 mt-3 text-[10px] uppercase tracking-widest text-white/80 group-hover:text-white transition-colors">
              Explore <span className="group-hover:translate-x-1 transition-transform">→</span>
            </span>
          </div>
        </Link>

        {/* Makeup */}
        <Link
          href={categories?.[1]?.href}
          className="reveal reveal-hidden group relative overflow-hidden aspect-[4/3] md:aspect-auto min-h-[220px] card-rounded">
          
          <AppImage
            src={categories?.[1]?.image}
            alt={categories?.[1]?.alt}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            className="object-cover product-card-img" />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 p-5 md:p-6">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">{categories?.[1]?.sub}</span>
            <h3 className="font-display text-2xl md:text-3xl font-light text-white">{categories?.[1]?.label}</h3>
          </div>
        </Link>

        {/* Fragrance */}
        <Link
          href={categories?.[2]?.href}
          className="reveal reveal-hidden group relative overflow-hidden aspect-[4/3] md:aspect-auto min-h-[220px] card-rounded">
          
          <AppImage
            src={categories?.[2]?.image}
            alt={categories?.[2]?.alt}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            className="object-cover product-card-img" />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 p-5 md:p-6">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">{categories?.[2]?.sub}</span>
            <h3 className="font-display text-2xl md:text-3xl font-light text-white">{categories?.[2]?.label}</h3>
          </div>
        </Link>

        {/* Haircare — col-span-2 */}
        <Link
          href={categories?.[3]?.href}
          className="reveal reveal-hidden group relative overflow-hidden aspect-[16/7] md:aspect-auto min-h-[200px] md:col-span-2 card-rounded">
          
          <AppImage
            src={categories?.[3]?.image}
            alt={categories?.[3]?.alt}
            fill
            sizes="(max-width: 768px) 100vw, 66vw"
            className="object-cover product-card-img" />
          
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-5 md:p-8">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">{categories?.[3]?.sub}</span>
            <h3 className="font-display text-2xl md:text-3xl font-light text-white">{categories?.[3]?.label}</h3>
          </div>
        </Link>
      </div>
    </section>);

}